import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { Subject, filter, skip, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import pkg from '../../package.json';
import { BrokerService, getQueryParamCaseInsensitive } from './core/broker/broker.service';
import { HeaderComponent } from './modules/shared/components/header/header.component';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { FooterComponent } from './modules/shared/components/footer/footer.component';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.sass'],
    imports: [HeaderComponent, RouterOutlet, FooterComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  activeUser: string | undefined = 'unknown user';
  username: string | undefined = '';
  isAuthenticated = false;

  private unsubscribe = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private broker: BrokerService,
    private route: ActivatedRoute
  ) {
    // Synchronous read: the ActivatedRoute snapshot isn't populated yet, and
    // the correct theme must be applied before first paint.
    const initial = getQueryParamCaseInsensitive(document.location.search, 'brokerId');
    this.broker.resolve(initial);
    // skip(1): the initial value was already handled synchronously above.
    this.route.queryParamMap
      .pipe(skip(1), takeUntilDestroyed())
      .subscribe((params) =>
        this.broker.resolve(
          params.get(params.keys.find((k) => k.toLowerCase() === 'brokerid') ?? 'brokerId')
        )
      );
  }
  ngOnDestroy(): void {
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }

  ngOnInit(): void {
    console.warn(environment.env);
    console.warn('Version: ' + pkg.version);

    // Process the AAD redirect response. Replaces the former
    // MsalRedirectComponent bootstrap component (removed with the
    // migration to standalone single-root bootstrapApplication).
    this.msalService.handleRedirectObservable().subscribe();
    // Account storage events (ACCOUNT_ADDED/ACCOUNT_REMOVED across tabs) are
    // emitted automatically by msal-browser v4+ when using localStorage.

    if (sessionStorage.getItem('sessionId') == null) {
      sessionStorage.setItem('sessionId', crypto.randomUUID());
    }

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this.unsubscribe)
      )
      .subscribe(() => {
        this.setAuthenticationStatus();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (message: EventMessage) =>
            message.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this.unsubscribe)
      )
      .subscribe((message: EventMessage) => {
        const authResult = message.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(authResult.account);

        if (sessionStorage.getItem('sessionId') == null) {
          sessionStorage.setItem('sessionId', crypto.randomUUID());
        }
      });
  }

  title = 'Workflow App';

  logout(): void {
    sessionStorage.removeItem('sessionId');
    this.msalService.instance.logoutRedirect();
  }

  setAuthenticationStatus(): void {
    let activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      activeAccount = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(activeAccount);
      
    }

    this.isAuthenticated = !activeAccount;
    this.activeUser = activeAccount?.username;
    this.username = activeAccount?.name;
  }
}
